import { apiFetch } from './api-client';
import { createContext, createElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { DEFAULT_PRESET_KEY, Gender, PERSONALITY_PRESETS, PersonalitySelection } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { API_BASE } from './api-base';

/**
 * The resolved name the UI should use for the primary assistant, so chrome ("Ask {short}", the chat
 * sender label, "Resolve with {short}") follows the name the user picked in Personality rather than a
 * hardcoded "Maestro".
 */
export interface AgentName {
  /** Full display name — "The Maestro" / "The Maestra" / a custom name. */
  name: string;
  /** Casual address form — the leading article stripped ("The Maestro" → "Maestro"). Same as `name` for a
   *  one-word custom name. This is the form for "Ask X" / "Talk to X" / "X's Brief". */
  short: string;
  /** The persona's presentation gender, for copy that must agree grammatically with the assistant (e.g.
   *  Hebrew "חושב" vs "חושבת"). Defaults to the unconfigured masculine default. */
  gender: Gender;
}

/** Strip a leading English article so "The Maestro" addresses as "Maestro"; a bare name is unchanged. */
export function agentShortForm(name: string): string {
  return name.replace(/^the\s+/i, '');
}

/** The name with nothing configured — native's `AssistantName.defaultFull`. */
export const DEFAULT_ASSISTANT_NAME = 'The Maestro';

const MAESTRO_DEFAULT: AgentName = { name: DEFAULT_ASSISTANT_NAME, short: 'Maestro', gender: 'male' };

const AgentNameContext = createContext<AgentName>(MAESTRO_DEFAULT);
/** Lets a descendant (the Personality settings screen) re-pull the name after a rename, so app-wide
 *  chrome updates without a hard reload. Default is a no-op. */
const AgentNameRefreshContext = createContext<() => void>(() => {});

/** Derive the display name from a selection, defensively: a custom name wins; else the preset's gendered
 *  name; else the preset default gender; else "The Maestro". Never returns undefined (a malformed gender
 *  must not throw in agentShortForm). */
function nameFromSelection(sel: PersonalitySelection): string {
  const preset = PERSONALITY_PRESETS.find((p) => p.key === sel.presetKey) ?? PERSONALITY_PRESETS.find((p) => p.key === DEFAULT_PRESET_KEY)!;
  return sel.name?.trim() || preset.nameByGender[sel.gender] || preset.nameByGender[preset.defaultGender] || MAESTRO_DEFAULT.name;
}

/**
 * Symphony-only provider: fetches the user's Personality selection and derives the resolved agent name
 * for the subtree. Re-fetchable via useRefreshAgentName so a rename reflects app-wide without a reload.
 * Falls back to "The Maestro" while loading or on error, so the UI never shows a raw token or empty name.
 * Mounted inside SymphonyApp.
 */
export function AgentNameProvider({ children }: { children: ReactNode }) {
  const [agent, setAgent] = useState<AgentName>(MAESTRO_DEFAULT);
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    let alive = true;
    apiFetch(`${API_BASE}/api/personality`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: { selection: PersonalitySelection }) => {
        if (!alive) {
          return;
        }
        const name = nameFromSelection(data.selection);
        setAgent({ name, short: agentShortForm(name), gender: data.selection.gender });
      })
      .catch(() => {}); // keep the Maestro default on failure — the gallery/name are non-critical chrome
    return () => {
      alive = false;
    };
  }, [nonce]);
  return createElement(AgentNameRefreshContext.Provider, { value: refresh }, createElement(AgentNameContext.Provider, { value: agent }, children));
}

/** The resolved agent name — the user's selected name, or the Maestro default. */
export function useAgentName(): AgentName {
  return useContext(AgentNameContext);
}

/** Re-pull the resolved agent name (call after saving a rename). */
export function useRefreshAgentName(): () => void {
  return useContext(AgentNameRefreshContext);
}
